/**
 * SocialTopics — Hashtag bazlı konu takip sayfası
 * Her hashtag bir konu başlığı gibi — altında o konuya ait postlar
 * Eski adı: SocialFollowing → yeni adı: Topics
 */
import { Hash, Plus, TrendingUp, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import MainLayout from '../components/layout/MainLayout'
import PostCard from '../components/social/PostCard'
import type { Post } from '../services/socialApi'
import { getPosts, getTrendingHashtags } from '../services/socialApi'

const STORAGE_KEY = 'runai_subscribed_topics'

function getSubscribed(): string[] {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as string[] }
  catch { return [] }
}
function saveSubscribed(topics: string[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(topics))
}

// Hashtag'i okunabilir başlığa çevir
function tagToTitle(tag: string): string {
  return tag
    .replace(/([A-Z])/g, ' $1')
    .replace(/[-_]/g, ' ')
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
    .trim()
}

export default function SocialFollowing() {
  const [subscribed, setSubscribed] = useState<string[]>(getSubscribed)
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(false)
  const [trendingHashtags, setTrendingHashtags] = useState<{ tag: string; post_count: number }[]>([])
  const [showDiscover, setShowDiscover] = useState(false)

  useEffect(() => {
    getTrendingHashtags(30).then(setTrendingHashtags).catch(() => {})
  }, [])

  useEffect(() => {
    if (subscribed.length === 0 && trendingHashtags.length > 0) {
      setShowDiscover(true)
    }
  }, [subscribed.length, trendingHashtags.length])

  useEffect(() => {
    if (!activeTag) { setPosts([]); return }
    setLoading(true)
    getPosts({ hashtag: activeTag, per_page: 30 })
      .then(d => setPosts(d.posts))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false))
  }, [activeTag])

  const subscribe = (tag: string) => {
    if (subscribed.includes(tag)) return
    const next = [...subscribed, tag]
    setSubscribed(next); saveSubscribed(next)
    setActiveTag(tag); setShowDiscover(false)
  }

  const unsubscribe = (tag: string) => {
    const next = subscribed.filter(t => t !== tag)
    setSubscribed(next); saveSubscribed(next)
    if (activeTag === tag) setActiveTag(next[0] ?? null)
  }

  const unsubscribedTrending = trendingHashtags.filter(h => !subscribed.includes(h.tag))

  return (
    <MainLayout>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Hash size={20} color="#667eea" /> Topics
            </h2>
            <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
              Follow topics to see posts from those discussions
            </p>
          </div>
          <button
            onClick={() => setShowDiscover(!showDiscover)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
              background: showDiscover ? 'var(--bg-secondary)' : 'linear-gradient(135deg, #667eea, #764ba2)',
              border: showDiscover ? '1px solid var(--border-default)' : 'none',
              color: showDiscover ? 'var(--text-secondary)' : '#fff', cursor: 'pointer'
            }}
          >
            <TrendingUp size={15} /> Discover Topics
          </button>
        </div>

        {/* Discover panel — trending hashtags as topic cards */}
        {showDiscover && (
          <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--border-color)',
            borderRadius: '12px', padding: '20px', marginBottom: '20px'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <TrendingUp size={14} color="#667eea" /> Trending Topics
            </div>
            {unsubscribedTrending.length === 0 && (
              <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>You follow all trending topics.</div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px' }}>
              {unsubscribedTrending.slice(0, 24).map(h => (
                <button
                  key={h.tag}
                  onClick={() => subscribe(h.tag)}
                  style={{
                    padding: '12px 14px', borderRadius: '10px', textAlign: 'left',
                    background: 'var(--bg-secondary)', border: '1px solid var(--border-default)',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#667eea'; e.currentTarget.style.background = '#667eea12' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.background = 'var(--bg-secondary)' }}
                >
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#667eea', marginBottom: 4 }}>#{h.tag}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 }}>{tagToTitle(h.tag)}</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: 4 }}>{h.post_count} posts</div>
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 4, color: '#667eea', fontSize: 11, fontWeight: 600 }}>
                    <Plus size={11} /> Follow
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {subscribed.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '64px 24px',
            background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px'
          }}>
            <Hash size={40} style={{ color: 'var(--text-muted)', margin: '0 auto 16px', display: 'block' }} />
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>No topics yet</div>
            <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '20px' }}>
              Follow topics to see posts from those discussions
            </div>
            <button
              onClick={() => setShowDiscover(true)}
              style={{
                padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                border: 'none', color: '#fff', cursor: 'pointer'
              }}
            >
              Discover Topics
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 20 }}>
            {/* LEFT: Topic list sidebar */}
            <div style={{ width: 200, flexShrink: 0 }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', marginBottom: 10, letterSpacing: '0.05em' }}>
                FOLLOWING ({subscribed.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {subscribed.map(tag => (
                  <div
                    key={tag}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 12px', borderRadius: '8px', cursor: 'pointer',
                      background: activeTag === tag ? '#667eea18' : 'transparent',
                      border: activeTag === tag ? '1px solid #667eea44' : '1px solid transparent',
                      transition: 'all 0.15s',
                    }}
                    onClick={() => setActiveTag(tag)}
                  >
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: activeTag === tag ? 700 : 500, color: activeTag === tag ? '#667eea' : 'var(--text-primary)' }}>
                        {tagToTitle(tag)}
                      </div>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>#{tag}</div>
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); unsubscribe(tag) }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, opacity: 0.6 }}
                    >
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT: Posts */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {!activeTag && (
                <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                  Select a topic to see posts
                </div>
              )}

              {activeTag && (
                <>
                  {/* Topic header */}
                  <div style={{ marginBottom: 16, padding: '14px 16px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px' }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)' }}>
                      {tagToTitle(activeTag)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#667eea', marginTop: 2 }}>#{activeTag}</div>
                  </div>

                  {loading && <div style={{ textAlign: 'center', padding: '32px', color: '#667eea' }}>Loading...</div>}

                  {!loading && posts.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-secondary)' }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>No posts for #{activeTag}</div>
                      <div style={{ fontSize: '13px' }}>Be the first to post about this topic</div>
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {posts.map(post => <PostCard key={post.id} post={post} />)}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  )
}
